import { Button, DropdownSelect, FieldWrapper, TextFieldWrapper, Colors, Table, TableCell, Badge } from "@factorearth/component-library";
import Form from "@factorearth/component-library/dist/Molecules/Form/Form";
import { Permission, Role, RoleForResolver, User, UserForResolver } from "db/definitions";
import useCustomForm from "hooks/useCustomForm";
import React, { useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import { useTheme } from "hooks/useSingleton";
import { FiPlus } from "react-icons/fi";

interface SelectOption {
	label: string;
	value: Permission;
}

interface Props {
	createRole: (role: Role) => Promise<void>;
	permissions: Permission[];
	roles: Role[];
	selectedRole?: Role;
	updateRole: (role: Role) => Promise<void>;
	userDoc: UserForResolver | User;
}

function RoleForm(props: Props) {
	const { createRole, permissions, selectedRole, updateRole, userDoc, roles } = props;

	const [disabled, setDisabled] = useState(false);
	const [openForm, setForm] = useState(false);
	const [timeStamp, setTimeStamp] = useState(new Date().toISOString());

	const colorPalette = useTheme();

	const [initialValues, setInitialValues] = useState<Role>({
		id: selectedRole?.id || uuid(),
		name: selectedRole?.name || "",
		description: selectedRole?.description || "",
		readGroups: selectedRole?.readGroups || userDoc.group === "FactorEarth" ? ["PERMISSION_DefaultTemplates"] : [],
		permissionIds: selectedRole?.permissionIds || [],
		permissions: selectedRole?.permissions || [],
		group: selectedRole?.group || userDoc.group,
		createdAt: selectedRole?.createdAt || timeStamp,
		updatedAt: selectedRole?.updatedAt || timeStamp,
	});

	useEffect(() => {
		setInitialValues({
			id: selectedRole?.id || uuid(),
			name: selectedRole?.name || "",
			description: selectedRole?.description || "",
			readGroups: selectedRole?.readGroups || userDoc.group === "FactorEarth" ? ["PERMISSION_DefaultTemplates"] : [],
			permissionIds: selectedRole?.permissionIds || [],
			permissions: selectedRole?.permissions || [],
			group: selectedRole?.group || userDoc.group,
			createdAt: selectedRole?.createdAt || timeStamp,
			updatedAt: selectedRole?.updatedAt || timeStamp,
		});
		setTimeStamp(new Date().toISOString());
	}, [openForm]);

	const { handleChange, handleSubmit, values } = useCustomForm(initialValues);

	function handleTextInputChange(e: React.ChangeEvent<HTMLInputElement>) {
		const event = {
			...e,
			target: {
				...e.target,
				name: e.target.name as keyof RoleForResolver,
				value: e.target.value as RoleForResolver[keyof RoleForResolver],
			},
		};
		handleChange(event);
	}

	function handleDropdownChange(e: SelectOption[]) {
		const ids = e.map((option) => option.value.id);
		const fakeEvent = {
			target: {
				name: "permissionIds" as keyof RoleForResolver,
				value: ids,
			},
		};
		const fakePermissionEvent = {
			target: {
				name: "permissions" as keyof Role,
				value: permissions.filter((perm) => ids.includes(perm.id)),
			},
		};
		handleChange(fakeEvent);
		handleChange(fakePermissionEvent);
	}

	const permissionOptions = useMemo(() => {
		return permissions.map((perm) => {
			return {
				label: perm.name,
				value: perm,
			};
		});
	}, [permissions, openForm]);

	const permissionValues = useMemo(() => {
		values.permissionIds.map((permId) => {
			const permission = permissions.find((perm) => perm.id === permId);
			if (permission) return { label: permission.name, value: permission };
		});
	}, [permissions, values.permissionIds, openForm]);

	async function saveButton() {
		setDisabled(true);
		await handleSubmit();
		selectedRole ? await updateRole(values) : await createRole(values);
		setDisabled(false);
		setForm(false);
	}

	const columnConfiguration = useMemo(() => {
		return [
			{
				key: "name",
				header: "Role",
				sortable: false,
				cell: (row: any) => (
					<TableCell colorPalette={colorPalette} style={{ padding: "8px 0px" }} key={`name-cell-for-${row.id}`}>
						<Badge theme={colorPalette} variant={"filled"} label={row.name} style={{ padding: "4px 8px" }} />
					</TableCell>
				),
			},
			{
				key: "permissions",
				header: "Permissions",
				cell: (row: any) => {
					const roles: any = [];
					row.permissions.forEach((permission: any) =>
						roles.push(
							<Badge
								theme={colorPalette}
								variant={"outlined"}
								label={permission.name}
								style={{ padding: "4px 8px" }}
								key={`permission-${permission.id}`}
							/>,
						),
					);
					return (
						<TableCell colorPalette={colorPalette} style={{ gap: "8px", padding: "8px 0px" }} key={`permissions-cell-for-${row.id}`}>
							{roles}
						</TableCell>
					);
				},
				sortable: false,
			},
		];
	}, [roles, openForm, colorPalette.theme]);

	return (
		<>
			{openForm ? (
				<Form colors={colorPalette}>
					<FieldWrapper colorPalette={colorPalette} label="Name">
						<TextFieldWrapper
							colorPalette={colorPalette}
							onChange={handleTextInputChange}
							value={values.name}
							aria-label="Name input"
							name="name"
						/>
					</FieldWrapper>
					<FieldWrapper colorPalette={colorPalette} label="Description">
						<TextFieldWrapper
							colorPalette={colorPalette}
							onChange={handleTextInputChange}
							value={values.description}
							aria-label="Description input"
							name="description"
						/>
					</FieldWrapper>
					<FieldWrapper colorPalette={colorPalette} label="Permissions">
						<DropdownSelect
							isMulti={true}
							value={permissionValues}
							aria-label="Value input"
							options={permissionOptions}
							// @ts-ignore
							onChange={handleDropdownChange}
							colorPalette={colorPalette}
						/>
					</FieldWrapper>
					<FieldWrapper colorPalette={colorPalette} label="Group">
						<TextFieldWrapper
							colorPalette={colorPalette}
							onChange={handleTextInputChange}
							disabled
							value={values.group}
							aria-label="Group input"
							name="group"
						/>
					</FieldWrapper>
					<div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", width: "100%" }}>
						<Button colorPalette={colorPalette} variant="destructive" onClick={() => setForm(false)} label="Delete Role" />
						<Button colorPalette={colorPalette} variant="outlined" onClick={() => setForm(false)} label="Exist Without Saving" />
						<Button
							colorPalette={colorPalette}
							variant="filled"
							onClick={saveButton}
							// @ts-ignore
							disabled={disabled}
							label="Create Role"
						/>
					</div>
				</Form>
			) : (
				<div style={{ width: "100%", background: colorPalette.background.primary }}>
					<div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
						<Button
							style={{ margin: "24px" }}
							colorPalette={colorPalette}
							variant="outlined"
							onClick={() => setForm(true)}
							label="Create New Role"
							icon={<FiPlus />}
						/>
					</div>
					<Table colorPalette={colorPalette} columnConfig={columnConfiguration} tableData={roles} pageSize={10} />
				</div>
			)}
		</>
	);
}

export default RoleForm;
